import { SystemIcons } from '@almbrand/systemicons';
import classNames from 'classnames';
import { useDesignSystemThemeName } from 'hooks/useDesignSystemThemeName';
import useOutsideClick from 'hooks/useOutsideClick';
import { useRef, useState } from 'react';
import styles from './Dropdown.module.scss';

interface DropdownItem {
	id: string;
	title: string;
	value: string;
	resetOption?: boolean;
}

interface DropdownProps {
	id: string;
	placeholder?: string;
	options: DropdownItem[];
	selectedId?: string;
	onSelect?: (item: DropdownItem) => void;
}

const Dropdown = ({ id, placeholder = 'Select', options, selectedId, onSelect }: DropdownProps) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const [selectedItem, setSelectedItem] = useState<DropdownItem | undefined>(
		selectedId ? options?.find((item) => item.id === selectedId) : undefined
	);
	const themeName = useDesignSystemThemeName();

	const handleChange = (item: DropdownItem) => {
		setSelectedItem(item.resetOption ? undefined : item);
		onSelect && onSelect(item);
		setIsOpen(false);
	};

	const dropdownRef = useRef<HTMLDivElement>(null);
	useOutsideClick({
		ref: dropdownRef,
		handler: () => setIsOpen(false),
	});

	return (
		<div
			ref={dropdownRef}
			className={styles.Dropdown}
		>
			<button
				id={id}
				aria-label='Toggle dropdown'
				aria-haspopup='true'
				aria-expanded={isOpen}
				type='button'
				onClick={() => setIsOpen(!isOpen)}
				className={classNames(
					styles.Dropdown__button,
					selectedItem && styles.Dropdown__button_selected,
					isOpen && styles.Dropdown__button_focused
				)}
			>
				<span>{selectedItem?.title || placeholder}</span>

				<span className={styles.Dropdown__icon}>
					<SystemIcons
						themeName={themeName}
						icon='chevron_down'
						width={'100%'}
						height={'100%'}
					/>
				</span>
			</button>

			{isOpen && (
				<div
					aria-label='Dropdown menu'
					className={styles.Dropdown__menuContainer}
				>
					<ul
						role='menu'
						aria-labelledby={id}
						aria-orientation='vertical'
						className={styles.Dropdown__menu}
					>
						{options?.map((item) => (
							<li
								key={item.id}
								onClick={() => handleChange(item)}
								onKeyDown={(event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										handleChange(item);
									}
								}}
								role='menuitem'
								tabIndex={0}
								className={classNames(
									styles.Dropdown__item,
									item.resetOption && styles.Dropdown__item_reset,
									selectedItem?.id === item.id && styles.Dropdown__item_active
								)}
							>
								<span>{item.title}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export default Dropdown;
